// Hand-crafted client for the CMS.Core.Backup REST API. Replaces the old
// auto-generated BackupApiFactory. When the @cms/backup-sdk package is introduced later,
// this file becomes a simple re-export.
//
// Goes through the shared `axios` instance (NOT raw fetch) so the Auth0AxiosBridge
// interceptor attaches the admin Bearer token on deployed instances — raw-fetch
// clients silently 401 there (same fix as contentByIdClient).
import axios from "axios";

export interface BackupJobDto {
	id: string;
	kind: string;
	scope: string;
	status: string;
	isDegraded: boolean;
	destinationId: string;
	instanceId?: string | null;
	startedAt: string;
	finishedAt?: string | null;
	bytesWritten: number;
	rowsExported: number;
	error?: string | null;
	correlationId: string;
}

export interface BackupDestinationDto {
	id: string;
	kind: string;
	enabled: boolean;
	primary: boolean;
	encryption: string;
	health: string;
}

export interface BackupInstanceDto {
	instanceId: string;
	lastSeenUtc: string;
	runningJobs: number;
	holdsGcLock: boolean;
	isCurrent: boolean;
}

export interface BackupMetricsDto {
	lastSuccessfulFullAgeSeconds: number | null;
	recentJobs: number;
	degradedJobs: number;
	failedJobs: number;
}

/** One archive actually present on a destination (read from its meta.json). */
export interface BackupArchiveDto {
	id: string;
	kind: string;
	scope: string;
	status: string;
	startedUtc: string;
	finishedUtc?: string | null;
	rowCount: number;
	bytesWritten: number;
	encryption: string;
	destinationId: string;
}

export interface RetentionResultDto {
	kept: string[];
	deleted: string[];
}

export interface RestoreResultDto {
	backupId: string;
	durationMs: number;
	tableDeltas: { table: string; inserts: number; updates: number; deletes: number; skips: number }[];
}

export interface BackupClient {
	listJobs(opts?: { skip?: number; take?: number }): Promise<BackupJobDto[]>;
	getJob(id: string): Promise<BackupJobDto>;
	createJob(body: { kind: string; scope: string; correlationId?: string }): Promise<BackupJobDto>;
	listDestinations(): Promise<BackupDestinationDto[]>;
	listInstances(): Promise<BackupInstanceDto[]>;
	getMetrics(): Promise<BackupMetricsDto>;
	listArchives(destinationId?: string): Promise<BackupArchiveDto[]>;
	deleteArchive(id: string, destinationId?: string): Promise<{ id: string; filesDeleted: number }>;
	applyRetention(destinationId?: string): Promise<RetentionResultDto>;
	restore(body: { backupId: string; destinationId: string; mode: "merge" | "replace"; tables?: string[]; ids?: Record<string, string[]> }): Promise<RestoreResultDto>;
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
const base = `${apiBase}/api/backup`;

const config = { withCredentials: true } as const;

export function createBackupClient(): BackupClient {
	return {
		listJobs: async (opts) => {
			const r = await axios.get<BackupJobDto[]>(`${base}/jobs`, {
				...config,
				params: { skip: opts?.skip, take: opts?.take },
			});
			return r.data ?? [];
		},
		getJob: async (id) => (await axios.get<BackupJobDto>(`${base}/jobs/${encodeURIComponent(id)}`, config)).data,
		createJob: async (body) => (await axios.post<BackupJobDto>(`${base}/jobs`, body, config)).data,
		listDestinations: async () => (await axios.get<BackupDestinationDto[]>(`${base}/destinations`, config)).data ?? [],
		listInstances: async () => (await axios.get<BackupInstanceDto[]>(`${base}/instances`, config)).data ?? [],
		getMetrics: async () => (await axios.get<BackupMetricsDto>(`${base}/metrics`, config)).data,
		listArchives: async (destinationId) => {
			const r = await axios.get<BackupArchiveDto[]>(`${base}/archives`, {
				...config,
				params: destinationId ? { destinationId } : undefined,
			});
			return r.data ?? [];
		},
		deleteArchive: async (id, destinationId) => {
			const r = await axios.delete<{ id: string; filesDeleted: number }>(
				`${base}/archives/${encodeURIComponent(id)}`,
				{
					...config,
					params: destinationId ? { destinationId } : undefined,
					headers: { "X-Confirm-Destructive": "I understand" },
				}
			);
			return r.data;
		},
		applyRetention: async (destinationId) => {
			const r = await axios.post<RetentionResultDto>(`${base}/retention/apply`, null, {
				...config,
				params: destinationId ? { destinationId } : undefined,
			});
			return r.data;
		},
		restore: async (body) => {
			const r = await axios.post<RestoreResultDto>(`${base}/restore`, body, {
				...config,
				headers: body.mode === "replace" ? { "X-Confirm-Destructive": "I understand" } : undefined,
			});
			return r.data;
		},
	};
}
